import { StrictMode, useEffect, useState, Component, lazy, Suspense } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { LoginPage } from './LoginPage.tsx'
import { captureCrashSnapshot, describeError, initClientReliability, reportAppCrash, reportClientEvent } from './reliability'
import { reloadOnceForStaleChunk, reloadOnceForPreloadError, noteSuccessfulBoot } from './utils/staleChunk'
import { STALE_CHUNK_RELOAD_MARKER } from './lib/boot/staleChunkReload'

// Boot-resilience handshake: this module executing at all PROVES the hashed
// entry bundle loaded, so clear the entry-bundle load-failure counter set by
// the inline classic guard in index.html (sessionStorage['har_boot_reload']).
// This resets the single-retry budget so a FUTURE deploy-propagation gap gets
// its own fresh guarded reload. Distinct key from staleChunk's guard.
try { sessionStorage.removeItem('har_boot_reload') } catch { /* ignore */ }

// Public-route SPA chunks. Loaded only when a public hash route matches —
// keeps the public bundle out of the main authed app.
const PublicProposalView = lazy(() => import('./views/inspections/PublicProposal'))
const ClientPortalApp = lazy(() => import('./views/portal/PortalApp'))

// Global crash guards. Without these, an unhandled error or rejection in
// any deep component could stop the React render tree and leave the user
// with Chrome's generic "This page is having a problem" / Crashpad dialog.
// We catch and log here, and surface a friendly in-app recovery card via
// the ErrorBoundary below. Long-running tabs (open all day) are most at
// risk because orphan listeners / leaked observers compound over time.
if (typeof window !== 'undefined') {
  initClientReliability()

  // Stale-chunk auto-reload. After a deploy, lazy chunks get new
  // content-hashed names and the old ones 404 (-> SPA fallback HTML), so a
  // tab still on the old index.js rejects the dynamic import when the user
  // navigates to a not-yet-loaded route. Detection + the one-time recovery
  // reload live in reloadOnceForStaleChunk (src/utils/staleChunk.ts), shared
  // with the React error boundaries here and in App.tsx (same reload guard).

  window.addEventListener('error', (e) => {
    const msg = e?.error?.message || e?.message || ''
    if (reloadOnceForStaleChunk(String(msg))) return
    try { console.error('[global-error]', e?.error?.message || e?.message || e, e?.error?.stack) } catch { /* ignore */ }
    reportClientEvent('global_error', describeError(e?.error || e?.message || e), 'error', true)
  })
  window.addEventListener('unhandledrejection', (e) => {
    const msg = (e?.reason as any)?.message || String(e?.reason || '')
    if (reloadOnceForStaleChunk(msg)) return
    try { console.error('[unhandled-rejection]', (e?.reason as any)?.message || e?.reason, (e?.reason as any)?.stack) } catch { /* ignore */ }
    reportClientEvent('unhandled_rejection', describeError(e?.reason), 'error', true)
  })

  // Vite fires `vite:preloadError` on ANY failed dynamic import / module
  // preload — the canonical, earliest signal of a stale chunk after a deploy
  // (marker: STALE_CHUNK_RELOAD_MARKER). Prevent the default (which would
  // rethrow the error) and hand off to the shared one-time, version+URL-guarded
  // recovery. If we already reloaded once for this exact bundle+URL (broken
  // deploy), reloadOnceForPreloadError returns false and we let the error
  // surface via reliability so Error Center keeps visibility.
  window.addEventListener('vite:preloadError', (e) => {
    const payload = (e as Event & { payload?: unknown }).payload
    if (reloadOnceForPreloadError(payload)) { try { e.preventDefault() } catch { /* ignore */ } ; return }
    try { console.error('[vite-preload-error]', (payload as any)?.message || payload) } catch { /* ignore */ }
    reportClientEvent('vite_preload_error', { ...describeError(payload), marker: STALE_CHUNK_RELOAD_MARKER }, 'error', true)
  })

  // Boot-resilience handshake for the stale-chunk guard: if we just reloaded to
  // recover a stale chunk, restore the single-reload budget once the (now
  // fresh) route re-imports without re-failing. Loop-safe — see noteSuccessfulBoot.
  noteSuccessfulBoot()
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null }
  static getDerivedStateFromError(err: Error) {
    // err-breadcrumbs-v1: stash the SANITIZED page snapshot NOW — this is the
    // last moment the crashed page's DOM is still committed (componentDidCatch
    // runs after React swaps in the recovery card). Read-only, never throws.
    captureCrashSnapshot()
    return { err }
  }
  componentDidCatch(err: Error, info: ErrorInfo) {
    // A failed lazy-import (stale chunk after a deploy) can land here too —
    // recover with a one-time reload instead of stranding on the card.
    if (reloadOnceForStaleChunk(err?.message)) return
    try { console.error('[react-error-boundary]', err?.message, info?.componentStack) } catch { /* ignore */ }
    reportClientEvent('react_error', { ...describeError(err), componentStack: info.componentStack }, 'error', true)
    // Error-ticket pipeline: severity=crash event with breadcrumbs + the
    // stashed sanitized snapshot (additive — react_error above is unchanged).
    reportAppCrash(err, info)
  }
  render() {
    if (!this.state.err) return this.props.children
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f9fc', fontFamily: 'system-ui, sans-serif', color: '#0f172a', padding: 24 }}>
        <div style={{ maxWidth: 480, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 28, boxShadow: '0 10px 30px rgba(15,23,42,0.08)' }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Something broke in the CRM.</div>
          <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, lineHeight: 1.5 }}>
            The app hit an error and stopped rendering. Reloading the page usually clears it. If it keeps happening, please screenshot the error below and send it to Max.
          </div>
          <pre style={{ background: '#0f172a', color: '#cbd5e1', padding: 12, borderRadius: 6, fontSize: 12, overflow: 'auto', maxHeight: 180, margin: 0 }}>{String(this.state.err?.message || this.state.err)}</pre>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button onClick={() => window.location.reload()} style={{ background: '#0a4f95', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Reload</button>
            <button onClick={() => this.setState({ err: null })} style={{ background: 'transparent', color: '#475569', border: '1px solid #cbd6e2', padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Try to recover</button>
          </div>
        </div>
      </div>
    )
  }
}

// Public-route exceptions — hash routes that should bypass the /api/me
// auth gate entirely. Used today by homeowner-link proposal signing,
// where the recipient has a one-shot bearer token in the URL and
// shouldn't be bounced to SSO. Add new patterns here when we ship more
// public-bearer flows.
const PUBLIC_ROUTE_PATTERNS: RegExp[] = [
  /^#\/public\//,
  /^#\/portal(?:\/|$)/,
]
function isPublicSpaRoute(hash: string): boolean {
  return PUBLIC_ROUTE_PATTERNS.some((re) => re.test(hash))
}
function isPortalSpaRoute(hash: string): boolean {
  return /^#\/portal(?:\/|$)/.test(hash)
}
function initialRootAuth(): 'checking' | 'public' | 'portal' {
  if (typeof window !== 'undefined' && isPortalSpaRoute(window.location.hash)) return 'portal'
  if (typeof window !== 'undefined' && isPublicSpaRoute(window.location.hash)) return 'public'
  return 'checking'
}

function Root() {
  const [auth, setAuth] = useState<'checking' | 'authed' | 'unauthed' | 'public' | 'portal'>(initialRootAuth)
  // Surfaced message from /api/me (e.g. "No BotPros employee profile found..."
  // for a user who passed SSO but has no HR record to auto-provision from).
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    // If the route is a public-bearer route (homeowner proposal link, etc.)
    // skip the /api/me auth round-trip entirely. The SPA will render in
    // public mode and use the URL token to fetch its own data.
    if (typeof window !== 'undefined' && isPublicSpaRoute(window.location.hash)) return
    fetch('/api/me', { credentials: 'include' })
      .then(async (r) => {
        // 403 from /api/me is meaningful: the user passed SSO but lacks an HR profile
        // or has been deactivated. Capture the message so the login page can show it.
        if (r.status === 403) {
          const data = await r.json().catch(() => ({}))
          setAuthError(data.error || 'Access denied.')
          return { authenticated: false }
        }
        if (!r.ok) return Promise.reject(r)
        return r.json()
      })
      .then((d) => setAuth(d && d.authenticated ? 'authed' : 'unauthed'))
      .catch(() => setAuth('unauthed'))
  }, [])

  // Once authed, replace any leftover /auth/... path with '/' so the URL bar
  // shows the clean SPA route instead of the SSO bounce URL.
  useEffect(() => {
    if (auth !== 'authed') return
    if (window.location.pathname.startsWith('/auth/')) {
      window.history.replaceState({}, '', '/' + window.location.hash)
    }
  }, [auth])

  if (auth === 'checking') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0b1220',
          color: '#6a85ad',
          fontFamily: 'system-ui, sans-serif',
          fontSize: 14,
        }}
      >
        Checking session…
      </div>
    )
  }
  if (auth === 'unauthed') return <LoginPage errorMessage={authError} />
  if (auth === 'public') {
    return (
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc', color: '#64748b', fontFamily: 'system-ui, sans-serif' }}>Loading…</div>
      }>
        <PublicProposalView />
      </Suspense>
    )
  }
  if (auth === 'portal') {
    return (
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#101923', color: '#b9c5d4', fontFamily: 'inherit' }}>Loading…</div>
      }>
        <ClientPortalApp />
      </Suspense>
    )
  }
  return <App />
}

// Ship marker: runtime string literal (survives minification) proving THIS
// BotPros build is the one being served. Asserted by live-verify.
document.documentElement.setAttribute('data-app-marker', 'jwm-crm-v1')
document.documentElement.setAttribute('data-instance', 'jwm-crm-v1')

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <Root />
    </AppErrorBoundary>
  </StrictMode>,
)
